export type NotificationMessage = {
  title: string;
  description?: string;
};

export type ProjectionToastKind = "thread" | "board" | "join-qr" | "artifact";

type ReplayStartCopyContext = {
  sessionTitle: string;
  promptSource: "draft" | "saved";
  judgeEnabled: boolean;
};

type ReplayStopCopyContext = {
  sessionTitle: string;
};

type SuggestionsGeneratedCopyContext = {
  createdCount: number;
  candidateCount: number;
  keptCount: number;
};

type SummaryPreviewCopyContext = {
  inputSectionCount: number;
  themeCount: number;
  narrativeCount: number;
};

type SummaryPublishedCopyContext = {
  inputSectionCount: number;
  themeCount: number;
};

type NotificationTree = {
  [key: string]: NotificationMessage | NotificationTree;
};

export const notificationCopy = {
  participant: {
    posted: { title: "Posted." },
    sentForReview: { title: "Sent for review." },
    replyPosted: { title: "Reply posted." },
    replySentForReview: { title: "Reply sent for review." },
    nowLive: { title: "Now live." },
    answered: { title: "Answered." },
    postingClosed: { title: "Posting is closed." },
    repliesClosed: { title: "Replies are closed." },
    joinBeforePosting: { title: "Join the session before posting." },
    sessionNotOpen: { title: "Session is not open." },
    threadClosedForReplies: { title: "Post is closed for replies." },
    couldNotPost: { title: "Could not post." },
    couldNotSendReply: { title: "Could not send reply." },
    couldNotUpdateName: { title: "Could not update name." },
    couldNotUpdateLike: { title: "Could not update like." },
  },
  moderator: {
    questionApproved: { title: "Question approved." },
    questionAnswered: { title: "Question marked answered." },
    questionHidden: { title: "Question hidden." },
    answerSaved: { title: "Answer saved." },
    updateFailed: { title: "Update failed.", description: "Please try again." },
  },
  staff: {
    couldNotUpdateStatus: { title: "Could not update status." },
    couldNotUpdateReply: { title: "Could not update reply." },
    couldNotUpdateText: { title: "Could not update text." },
    couldNotUpdateScreen: { title: "Could not update screen." },
  },
  admin: {
    sessionCreated: { title: "Session created." },
    sessionUpdated: { title: "Session updated." },
    promptSaved: { title: "Prompt saved." },
    promptReset: { title: "Prompt reset." },
    settingsSaved: { title: "Settings saved." },
    pricingSaved: { title: "Pricing saved." },
    sessionArchived: { title: "Session archived." },
    sessionDeleted: { title: "Session deleted." },
    switchCommandSent: { title: "Switch command sent." },
    sessionIdCopied: { title: "Session ID copied." },
    contextSaved: { title: "Context saved." },
    summarySettingsSaved: { title: "Summary settings saved." },
    summaryShown: { title: "Summary shown." },
    summaryHidden: { title: "Summary hidden." },
    summaryArchived: { title: "Summary archived." },
    correctionsApplied: { title: "Corrections applied." },
    summariesRepaired: { title: "Summaries repaired." },
    blockedSuggestionsRetried: { title: "Blocked suggestions queued for retry." },
    blockedSuggestionsSkipped: { title: "Blocked suggestion moments resolved." },
    couldNotSavePrompt: { title: "Could not save prompt." },
    couldNotResetPrompt: { title: "Could not reset prompt." },
    couldNotStartReplay: { title: "Could not start replay." },
    couldNotStopReplay: { title: "Could not stop replay." },
    couldNotSavePricing: { title: "Could not save pricing." },
    couldNotArchiveSession: { title: "Could not archive session." },
    couldNotDeleteSession: { title: "Could not delete session." },
    couldNotGenerateQuestions: { title: "Could not generate questions." },
    couldNotRecoverSuggestions: { title: "Could not recover blocked suggestions." },
    couldNotQueue: { title: "Could not queue." },
    couldNotCopy: { title: "Could not copy." },
    couldNotSaveContext: { title: "Could not save context." },
    couldNotSaveSummarySettings: { title: "Could not save summary settings." },
    couldNotGenerateSummary: { title: "Could not generate summary." },
    couldNotUpdateSummary: { title: "Could not update summary." },
    couldNotConsolidate: { title: "Could not consolidate." },
    couldNotPublish: { title: "Could not publish." },
    actionFailed: { title: "Action failed.", description: "Please try again." },
  },
  shared: {
    copied: { title: "Copied." },
    saved: { title: "Saved." },
    deleted: { title: "Deleted." },
    networkError: { title: "Connection issue.", description: "Check your connection and try again." },
    unexpectedError: { title: "Something went wrong.", description: "Please try again." },
  },
} satisfies NotificationTree;

export function postingTooQuicklyCopy(
  retryAfterSeconds: number | null,
  action: "post" | "reply" = "post",
): NotificationMessage {
  return {
    title: action === "reply" ? "You're replying quickly." : "You're posting quickly.",
    description: retryAfterSeconds !== null
      ? `Try again in ${retryAfterSeconds} seconds.`
      : "Try again shortly.",
  };
}

export function sessionsDeletedCopy(count: number): NotificationMessage {
  return {
    title: `Deleted ${count} ${count === 1 ? "session" : "sessions"}.`,
  };
}

export function projectionThreadShownCopy(body: string, showReplies: boolean): NotificationMessage {
  const snippet = notificationSnippet(body);
  return {
    title: "Projected question on screen.",
    description: joinDescription(snippet, showReplies ? "Replies are visible." : "Replies are hidden."),
  };
}

export function projectionBoardShownCopy(sortLabel: string): NotificationMessage {
  return {
    title: "Projected audience board.",
    description: `Board sort set to ${sortLabel}.`,
  };
}

export function projectionJoinQrShownCopy(joinCode?: string | null): NotificationMessage {
  return {
    title: "Projected join QR.",
    description: joinCode ? `Join code ${joinCode} is on screen.` : "Join QR is on screen.",
  };
}

export function projectionRemovedCopy(kind: ProjectionToastKind, detail?: string): NotificationMessage {
  const snippet = detail ? notificationSnippet(detail) : undefined;
  if (kind === "thread") {
    return {
      title: "Removed question from screen.",
      description: snippet,
    };
  }
  if (kind === "join-qr") {
    return {
      title: "Removed join QR from screen.",
      description: "Audience board is back on screen.",
    };
  }
  if (kind === "artifact") {
    return {
      title: "Removed artifact from screen.",
      description: snippet,
    };
  }
  return {
    title: "Reset projected board.",
    description: snippet,
  };
}

export function projectedRepliesVisibilityCopy(visible: boolean, threadBody?: string): NotificationMessage {
  return {
    title: visible ? "Projected replies shown." : "Projected replies hidden.",
    description: threadBody ? notificationSnippet(threadBody) : undefined,
  };
}

export function threadStatusCopy(
  status: "approved" | "answered" | "reopened" | "hidden",
  body: string,
): NotificationMessage {
  const title = status === "approved"
    ? "Approved question."
    : status === "answered"
      ? "Marked question answered."
      : status === "reopened"
        ? "Reopened question."
        : "Hidden question.";
  return {
    title,
    description: notificationSnippet(body),
  };
}

export function threadEditSavedCopy(body: string): NotificationMessage {
  return {
    title: "Saved question edit.",
    description: notificationSnippet(body),
  };
}

export function replyStatusCopy(status: "approved" | "hidden", body?: string): NotificationMessage {
  return {
    title: status === "approved" ? "Approved reply." : "Hidden reply.",
    description: body ? notificationSnippet(body) : undefined,
  };
}

export function staffPostQueuedCopy(body: string): NotificationMessage {
  return {
    title: "Queued staff post.",
    description: notificationSnippet(body),
  };
}

export function replayStartedCopy(context: ReplayStartCopyContext): NotificationMessage {
  return {
    title: "Started replay run.",
    description: `${context.sessionTitle} using ${context.promptSource === "draft" ? "draft override" : "saved prompt"}${context.judgeEnabled ? " with judge enabled." : "."}`,
  };
}

export function replayStoppedCopy(context: ReplayStopCopyContext): NotificationMessage {
  return {
    title: "Stopped replay run.",
    description: context.sessionTitle,
  };
}

export function suggestionsGeneratedCopy(context: SuggestionsGeneratedCopyContext): NotificationMessage {
  return {
    title: `Generated ${context.createdCount} ${pluralize(context.createdCount, "question")}.`,
    description: `${context.keptCount} kept from ${context.candidateCount} ${pluralize(context.candidateCount, "candidate")}.`,
  };
}

export function noNewQuestionsCopy(reason?: string): NotificationMessage {
  return {
    title: "No new questions generated.",
    description: reason ? sentenceFromReason(reason) : "The generation pass did not produce any new approved questions.",
  };
}

export function manualSummaryGenerationCopy(message: string, provider: string): NotificationMessage {
  return {
    title: "Generated timeline summary.",
    description: `${message} Provider: ${provider}.`,
  };
}

export function manualSummaryInfoCopy(message: string): NotificationMessage {
  return {
    title: "No new timeline summary.",
    description: message,
  };
}

export function summaryPreviewReadyCopy(context: SummaryPreviewCopyContext): NotificationMessage {
  return {
    title: "Consolidated preview ready.",
    description: `${context.inputSectionCount} ${pluralize(context.inputSectionCount, "section")}, ${context.themeCount} ${pluralize(context.themeCount, "theme")}, ${context.narrativeCount} ${pluralize(context.narrativeCount, "arc point")}.`,
  };
}

export function summaryPublishedCopy(context: SummaryPublishedCopyContext): NotificationMessage {
  return {
    title: "Published consolidated summary.",
    description: `${context.inputSectionCount} ${pluralize(context.inputSectionCount, "section")} merged into ${context.themeCount} ${pluralize(context.themeCount, "theme")}.`,
  };
}

export function participantJoinedCopy(sessionTitle: string, alias: string): NotificationMessage {
  return {
    title: `Joined ${sessionTitle}.`,
    description: `Posting as ${alias}.`,
  };
}

export function sessionMovedCopy(sessionTitle: string): NotificationMessage {
  return {
    title: `Moved to ${sessionTitle}.`,
  };
}

export function participantMoveNeedsJoinCopy(
  sessionTitle: string,
  reason?: string,
): NotificationMessage {
  return {
    title: `Moved to ${sessionTitle}, but could not join automatically.`,
    description: reason
      ? `${reason} Use the join form to continue.`
      : "Use the join form to continue.",
  };
}

export function participantNameUpdatedCopy(alias: string): NotificationMessage {
  return {
    title: "Display name updated.",
    description: `Now posting as ${alias}.`,
  };
}

export function participantSubmissionLiveCopy(body: string): NotificationMessage {
  return {
    title: "Your submission is now live.",
    description: notificationSnippet(body),
  };
}

export function participantThreadAnsweredCopy(body: string): NotificationMessage {
  return {
    title: "Your post was marked answered.",
    description: notificationSnippet(body),
  };
}

export function notificationSnippet(text: string, maxLength = 96): string | undefined {
  const compact = text.replace(/\s+/g, " ").trim();
  if (!compact) return undefined;
  const truncated = compact.length > maxLength
    ? `${compact.slice(0, maxLength - 3).trimEnd()}...`
    : compact;
  return `"${truncated}"`;
}

function joinDescription(...parts: Array<string | undefined>): string | undefined {
  const present = parts.filter((part): part is string => Boolean(part));
  return present.length > 0 ? present.join(" ") : undefined;
}

function pluralize(count: number, singular: string): string {
  return `${singular}${count === 1 ? "" : "s"}`;
}

function sentenceFromReason(reason: string): string {
  const normalized = reason.replace(/_/g, " ").trim();
  if (!normalized) return "The generation pass did not produce any new approved questions.";
  return `${normalized.charAt(0).toUpperCase()}${normalized.slice(1)}.`;
}
